import React, { useCallback, useState } from "react";
import styled from "styled-components";
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    TextField,
    Typography
} from "@mui/material";
import firebase from "firebase";
import { auth, firestore } from "../services/firebase";
import { captureError } from "../lib/errorReporting";

export default function SignupCard({
    title,
    description,
    onSuccess,
    afterAuthRoute
}) {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [info, setInfo] = useState("");
    const [notice, setNotice] = useState("");
    const [step, setStep] = useState("email");
    const [accountExists, setAccountExists] = useState(false);
    const [credentials, setCredentials] = useState({
        email: "",
        password: "",
        confirmPassword: ""
    });

    const handleCredentialChange = (event) => {
        const { name, value } = event.target;
        setCredentials((prev) => ({
            ...prev,
            [name]: value
        }));
    };

    const ensureUserDocument = useCallback(async (user) => {
        const email = user.email?.toLowerCase() || "";
        const usersCollection = firestore.collection("Users");
        const uidRef = usersCollection.doc(user.uid);
        const uidSnapshot = await uidRef.get();

        let targetRef = uidRef;
        let existingData = uidSnapshot.exists ? uidSnapshot.data() : null;

        if (!uidSnapshot.exists) {
            const emailMatch = await usersCollection
                .where("email", "==", email)
                .limit(1)
                .get();
            if (!emailMatch.empty) {
                targetRef = emailMatch.docs[0].ref;
                existingData = emailMatch.docs[0].data();
            }
        }

        const basePayload = {
            email,
            has_updated_categories: false,
            last_event_signup_at: firebase.firestore.FieldValue.serverTimestamp()
        };

        if (!existingData || existingData?.unassigned_to_bar === undefined) {
            basePayload.unassigned_to_bar = true;
        }

        if (!existingData || existingData?.onBoardingComplete === undefined) {
            basePayload.onBoardingComplete = false;
        }

        if (existingData) {
            await targetRef.set(basePayload, { merge: true });
            return existingData;
        }

        await targetRef.set(
            {
                roles: {},
                isAdmin: false,
                visibility: true,
                token: "",
                onBoardingComplete: false,
                ...basePayload
            },
            { merge: true }
        );
        return null;
    }, []);

    const handleEmailContinue = async (event) => {
        event.preventDefault();
        const email = credentials.email.trim();
        if (!email) {
            setError("Enter your email to continue.");
            return;
        }
        setLoading(true);
        setError("");
        setInfo("");
        setNotice("");
        try {
            const methods = await auth.fetchSignInMethodsForEmail(email);
            setAccountExists(methods.length > 0);
            setStep("password");
        } catch (lookupError) {
            captureError(lookupError, { component: "SignupCard", action: "lookupEmail" });
            setError(
                lookupError?.message || "Unable to check that email. Please try again."
            );
        } finally {
            setLoading(false);
        }
    };

    const handleChangeEmail = () => {
        setStep("email");
        setError("");
        setInfo("");
        setNotice("");
        setCredentials((prev) => ({
            ...prev,
            password: "",
            confirmPassword: ""
        }));
    };

    const handleForgotPassword = async () => {
        const email = credentials.email.trim();
        if (!email) {
            setError("Enter your email first, then select Forgot password.");
            return;
        }
        setLoading(true);
        setError("");
        setInfo("");
        setNotice("");
        try {
            await auth.sendPasswordResetEmail(email);
            setInfo("Password reset email sent. Check your inbox.");
        } catch (resetError) {
            captureError(resetError, { component: "SignupCard", action: "forgotPassword" });
            setError(
                resetError?.message || "Unable to send reset email. Please try again."
            );
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        const email = credentials.email.trim();
        if (!accountExists && credentials.password !== credentials.confirmPassword) {
            setError("Passwords must match.");
            return;
        }
        setLoading(true);
        setError("");
        setInfo("");
        setNotice("");
        try {
            let userCredential;
            if (accountExists) {
                userCredential = await auth.signInWithEmailAndPassword(
                    email,
                    credentials.password
                );
            } else {
                userCredential = await auth.createUserWithEmailAndPassword(
                    email,
                    credentials.password
                );
            }

            const { user } = userCredential;
            const existingData = await ensureUserDocument(user);
            if (onSuccess) {
                await onSuccess(user, existingData);
            }
            if (afterAuthRoute) {
                window.location.assign(afterAuthRoute);
            }
        } catch (submitError) {
            // Firebase email-enumeration protection hides existing accounts from
            // fetchSignInMethodsForEmail, so a "create account" attempt surfaces the
            // conflict here. Flip to login instead of showing a hard error.
            if (submitError?.code === "auth/email-already-in-use") {
                setAccountExists(true);
                setCredentials((prev) => ({ ...prev, confirmPassword: "" }));
                setNotice(
                    "This email is already in use, please log in to continue claiming your bar."
                );
                return;
            }
            captureError(submitError, { component: "SignupCard", action: "signupOrSignIn" });
            setError(
                submitError?.message || "Unable to continue. Please try again."
            );
        } finally {
            setLoading(false);
        }
    };

    return (
        <OuterContainer>
            <Card>
                <HeaderLogo src="/logo2.png" alt="Bar Hopper" />
                <Typography component="h1" variant="h4" gutterBottom align="center">
                    {title}
                </Typography>
                {description && (
                    <Typography variant="body1" paragraph align="center">
                        {description}
                    </Typography>
                )}
                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}
                {info && (
                    <Alert severity="success" sx={{ mb: 2 }}>
                        {info}
                    </Alert>
                )}
                {notice && (
                    <Alert severity="info" sx={{ mb: 2 }}>
                        {notice}
                    </Alert>
                )}
                {step === "email" ? (
                    <form onSubmit={handleEmailContinue}>
                        <Box display="flex" flexDirection="column" gap={2}>
                            <TextField
                                label="Email"
                                name="email"
                                type="email"
                                value={credentials.email}
                                onChange={handleCredentialChange}
                                required
                                fullWidth
                                autoFocus
                            />
                            <Button
                                variant="contained"
                                color="primary"
                                type="submit"
                                disabled={!credentials.email || loading}
                                endIcon={loading ? <CircularProgress size={18} /> : null}
                            >
                                Continue
                            </Button>
                        </Box>
                    </form>
                ) : (
                    <form onSubmit={handleSubmit}>
                        <Box display="flex" flexDirection="column" gap={2}>
                            <TextField
                                label="Email"
                                name="email"
                                type="email"
                                value={credentials.email}
                                fullWidth
                                disabled
                            />
                            <TextField
                                label="Password"
                                name="password"
                                type="password"
                                value={credentials.password}
                                onChange={handleCredentialChange}
                                required
                                fullWidth
                                autoFocus
                                inputProps={{ minLength: 6 }}
                            />
                            {!accountExists && (
                                <TextField
                                    label="Confirm Password"
                                    name="confirmPassword"
                                    type="password"
                                    value={credentials.confirmPassword}
                                    onChange={handleCredentialChange}
                                    required
                                    fullWidth
                                    inputProps={{ minLength: 6 }}
                                />
                            )}
                            <Button
                                variant="contained"
                                color="primary"
                                type="submit"
                                disabled={
                                    !credentials.password ||
                                    (!accountExists && !credentials.confirmPassword) ||
                                    loading
                                }
                                endIcon={loading ? <CircularProgress size={18} /> : null}
                            >
                                {accountExists ? "Login" : "Create Account"}
                            </Button>
                            <Box
                                display="flex"
                                justifyContent="space-between"
                                alignItems="center"
                                flexWrap="wrap"
                            >
                                <Button
                                    type="button"
                                    variant="text"
                                    size="small"
                                    onClick={handleChangeEmail}
                                    disabled={loading}
                                >
                                    Use a different email
                                </Button>
                                <Button
                                    type="button"
                                    variant="text"
                                    size="small"
                                    onClick={handleForgotPassword}
                                    disabled={loading}
                                >
                                    Forgot password?
                                </Button>
                            </Box>
                        </Box>
                    </form>
                )}
            </Card>
        </OuterContainer>
    );
}

const OuterContainer = styled.div`
    position: relative;
    min-height: 100dvh;
    width: 100%;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    padding: 24px 16px;
    background: linear-gradient(
        0deg,
        rgba(0, 0, 0, 1) 0%,
        rgba(196, 45, 62, 1) 100%
    );
    overflow-y: auto;
`;

const Card = styled.div`
    position: relative;
    z-index: 1;
    max-width: 420px;
    width: 100%;
    background: #f0f0f0;
    border-radius: 4px;
    padding: 32px;
    box-shadow: 2px 2px 2px rgba(0, 0, 0, 0.2);
    display: flex;
    flex-direction: column;
    align-items: stretch;
    gap: 16px;

    form {
        width: 100%;
    }

    @media (max-width: 600px) {
        padding: 28px 24px;
    }
`;

const HeaderLogo = styled.img`
    width: 200px;
    height: auto;
    margin: 0 auto 8px;
    pointer-events: none;
    user-select: none;

    @media (max-width: 480px) {
        width: 140px;
    }
`;
